import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, type EotpDataDto, type EotpremnicaDto, type EotpValidationDto } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

const inputCls = "h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none";
const STATUS: Record<string, { t: string; c: string }> = {
  DRAFT: { t: "Nacrt", c: "bg-slate-100 text-slate-700" },
  SENDING: { t: "Poslato, čeka obradu", c: "bg-amber-100 text-amber-800" },
  REGISTERED: { t: "Registrovana", c: "bg-emerald-100 text-emerald-800" },
  FAILED: { t: "Greška", c: "bg-red-100 text-red-700" },
  CANCELLING: { t: "Storno u obradi", c: "bg-amber-100 text-amber-800" },
  CANCELLED: { t: "Stornirana", c: "bg-slate-200 text-slate-600" },
};
const METHODS: Record<number, string> = { 1: "Sopstveni prevoz", 2: "Prevoznik", 3: "Prevoz primaoca", 4: "Lično preuzimanje", 5: "Lična dostava" };

function errMsg(e: unknown) {
  if (e instanceof ApiError) {
    const m = e.response.message;
    return Array.isArray(m) ? m.join(" ") : m;
  }
  return e instanceof Error ? e.message : "Radnja nije uspela.";
}

function emptyData(def: { actualDespatchAt: string; plannedDeliveryEndAt: string }): EotpDataDto {
  return { recipient: { name: "" }, method: 1, items: [{ name: "", quantity: 1, unit: "kom" }], ...def };
}

export function EotpremnicePage() {
  const qc = useQueryClient();
  const [editId, setEditId] = useState<string | "new" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const list = useQuery({ queryKey: ["eotp-list"], queryFn: () => api.listEotpremnice(), retry: false, refetchInterval: (q) => (q.state.data?.items.some((i) => i.status === "SENDING" || i.status === "CANCELLING") ? 15000 : false) });
  const settings = useQuery({ queryKey: ["eotp-settings"], queryFn: () => api.getEotpSettings(), retry: false });
  const sync = useMutation({
    mutationFn: () => api.syncEotpremnice(),
    onSuccess: (r) => { setErr(null); setInfo(r.updated ? `Osveženo: ${r.updated}.` : "Nema novih promena."); qc.invalidateQueries({ queryKey: ["eotp-list"] }); },
    onError: (e) => setErr(errMsg(e)),
  });
  // kada ima poslatih otpremnica, povremeno osveži statuse iz sistema
  const pending = list.data?.items.some((i) => i.status === "SENDING" || i.status === "CANCELLING");
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => sync.mutate(), 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  if (list.isError) {
    if (list.error instanceof ApiError && list.error.response.errorCode === "MODULE_NOT_AVAILABLE") return <ModuleGateNotice error={list.error} moduleKey="eotpremnice" />;
    return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">Učitavanje nije uspelo.</p>;
  }
  if (editId) return <Editor id={editId === "new" ? null : editId} onBack={() => { setEditId(null); qc.invalidateQueries({ queryKey: ["eotp-list"] }); }} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">eOtpremnice</h1>
          <p className="text-sm text-slate-500">Izdavanje i praćenje elektronskih otpremnica preko sistema Ministarstva finansija.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => sync.mutate()} disabled={sync.isPending}>{sync.isPending ? "Osvežavam…" : "Osveži statuse"}</Button>
          <Button onClick={() => setEditId("new")}>Nova otpremnica</Button>
        </div>
      </div>
      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
      {info && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{info}</p>}
      <SettingsCard data={settings.data} />
      {list.isLoading ? <Skeleton className="h-32" /> : (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-subtle">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr><th className="px-3 py-2">Broj</th><th className="px-3 py-2">Primalac</th><th className="px-3 py-2">Datum</th><th className="px-3 py-2">Status</th><th className="px-3 py-2" /></tr>
            </thead>
            <tbody>
              {(list.data?.items ?? []).map((r) => {
                const st = STATUS[r.status] ?? { t: r.status, c: "bg-slate-100 text-slate-700" };
                return (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">{r.number}</td>
                    <td className="px-3 py-2">{r.recipient_name}<div className="text-xs text-slate-400">{r.recipient_pib}</div></td>
                    <td className="px-3 py-2">{r.issue_date ?? new Date(r.created_at).toLocaleDateString("sr-RS")}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${st.c}`}>{st.t}</span>
                      {r.remote_status_label && <div className="mt-0.5 text-xs text-slate-500">{r.remote_status_label}</div>}
                      {r.last_error && <div className="mt-0.5 max-w-xs text-xs text-red-600">{r.last_error}</div>}
                    </td>
                    <td className="px-3 py-2 text-right"><Button variant="secondary" onClick={() => setEditId(r.id)}>Otvori</Button></td>
                  </tr>
                );
              })}
              {!list.data?.items.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Još nema otpremnica.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SettingsCard({ data }: { data?: { has_key: boolean; key_hint: string | null; mode: "DEMO" | "PROD" } }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [mode, setMode] = useState<"DEMO" | "PROD">("DEMO");
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (data) setMode(data.mode); }, [data]);
  const save = useMutation({
    mutationFn: () => api.saveEotpSettings({ api_key: key.trim() || undefined, mode }),
    onSuccess: () => { setKey(""); setErr(null); setOpen(false); qc.invalidateQueries({ queryKey: ["eotp-settings"] }); },
    onError: (e) => setErr(errMsg(e)),
  });
  return (
    <div className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm">
          API ključ: {data?.has_key ? <b>{data.key_hint}</b> : <span className="text-red-600">nije unet</span>} · okruženje: <b>{data?.mode === "PROD" ? "produkcija" : "demo"}</b>
        </p>
        <Button variant="secondary" onClick={() => setOpen(!open)}>Podešavanje</Button>
      </div>
      {open && (
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <label className="text-xs text-slate-500 sm:col-span-2">API ključ (iz portala eOtpremnica → Podešavanja)
            <input className={inputCls} type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder={data?.has_key ? "ostavite prazno da zadržite postojeći" : "nalepite ključ"} />
          </label>
          <label className="text-xs text-slate-500">Okruženje
            <select className={inputCls} value={mode} onChange={(e) => setMode(e.target.value as "DEMO" | "PROD")}>
              <option value="DEMO">Demo (za probu)</option><option value="PROD">Produkcija</option>
            </select>
          </label>
          {err && <p className="text-sm text-red-600 sm:col-span-3">{err}</p>}
          <div className="sm:col-span-3"><Button onClick={() => save.mutate()} disabled={save.isPending}>Sačuvaj</Button></div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children, className = "" }: { label: string; children: React.ReactNode; className?: string }) => (
  <label className={`text-xs text-slate-500 ${className}`}>{label}{children}</label>
);

function Editor({ id, onBack }: { id: string | null; onBack: () => void }) {
  const qc = useQueryClient();
  const [rec, setRec] = useState<EotpremnicaDto | null>(null);
  const [number, setNumber] = useState("");
  const [d, setD] = useState<EotpDataDto | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [val, setVal] = useState<EotpValidationDto | null>(null);
  const [reason, setReason] = useState("");
  const [fromDoc, setFromDoc] = useState("");

  const load = useQuery({
    queryKey: ["eotp-one", id],
    queryFn: async () => (id ? api.getEotpremnica(id) : null),
    retry: false,
  });
  const defs = useQuery({ queryKey: ["eotp-defaults"], queryFn: () => api.eotpDefaults(), retry: false });
  useEffect(() => {
    if (id && load.data) { setRec(load.data); setNumber(load.data.number); setD(load.data.data ?? null); }
    if (!id && defs.data && !d) setD(emptyData(defs.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load.data, defs.data]);

  const editable = !rec || rec.status === "DRAFT" || rec.status === "FAILED";
  const set = (patch: Partial<EotpDataDto>) => setD((p) => (p ? { ...p, ...patch } : p));
  const setRecipient = (patch: Partial<EotpDataDto["recipient"]>) => setD((p) => (p ? { ...p, recipient: { ...p.recipient, ...patch } } : p));

  const apply = (r: EotpremnicaDto) => { setRec(r); setNumber(r.number); setD(r.data ?? d); qc.invalidateQueries({ queryKey: ["eotp-one", r.id] }); };

  const save = useMutation({
    mutationFn: async () => {
      if (!d) throw new Error("Nema podataka.");
      const payload = { number: number.trim() || undefined, data: clean(d) };
      return api.saveEotpremnica(rec?.id ?? null, payload);
    },
    onSuccess: (r) => { setErr(null); setInfo("Sačuvano."); apply(r); },
    onError: (e) => setErr(errMsg(e)),
  });
  const doValidate = useMutation({
    mutationFn: async () => { const r = await save.mutateAsync(); return api.validateEotpremnica(r.id); },
    onSuccess: (v) => { setErr(null); setVal(v); },
    onError: (e) => setErr(errMsg(e)),
  });
  const doSend = useMutation({
    mutationFn: async () => { const r = await save.mutateAsync(); return api.sendEotpremnica(r.id); },
    onSuccess: (r) => { setErr(null); setInfo("Otpremnica je poslata. Status se osvežava automatski."); apply(r); },
    onError: (e) => setErr(errMsg(e)),
  });
  const doCancel = useMutation({
    mutationFn: () => api.cancelEotpremnica(rec!.id, reason),
    onSuccess: (r) => { setErr(null); setInfo("Storno je poslat."); apply(r); },
    onError: (e) => setErr(errMsg(e)),
  });
  const del = useMutation({ mutationFn: () => api.deleteEotpremnica(rec!.id), onSuccess: onBack, onError: (e) => setErr(errMsg(e)) });
  const importDoc = useMutation({
    mutationFn: () => api.eotpremnicaFromDocument(fromDoc.trim()),
    onSuccess: (r) => { setErr(null); apply(r); },
    onError: (e) => setErr(errMsg(e)),
  });
  const open = async (what: "xml" | "pdf") => {
    try { const u = await api.downloadEotpremnicaBlobUrl(rec!.id, what); window.open(u, "_blank"); } catch (e) { setErr(errMsg(e)); }
  };

  if (!d) return <Skeleton className="h-40" />;
  const st = rec ? STATUS[rec.status] : STATUS.DRAFT;
  const busy = save.isPending || doSend.isPending || doValidate.isPending;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={onBack}>← Nazad</Button>
          <h1 className="text-xl font-semibold text-slate-900">{rec ? `Otpremnica ${rec.number}` : "Nova otpremnica"}</h1>
          {st && <span className={`rounded-full px-2 py-0.5 text-xs ${st.c}`}>{st.t}</span>}
        </div>
        {rec?.remote_status_label && <span className="text-sm text-slate-500">{rec.remote_status_label}</span>}
      </div>
      {err && <p className="whitespace-pre-line rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
      {info && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{info}</p>}
      {rec?.last_error && !err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{rec.last_error}</p>}
      {val && (
        <div className={`rounded-lg px-3 py-2 text-sm ${val.has_errors ? "bg-red-50 text-red-700" : val.has_warnings ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700"}`}>
          {val.is_valid ? (val.has_warnings ? "Dokument je ispravan, ali ima upozorenja:" : "Dokument je ispravan i spreman za slanje.") : "Dokument ima greške:"}
          {val.messages.map((m, i) => <div key={i} className="text-xs">• [{m.severity}] {m.description}</div>)}
        </div>
      )}

      {!rec && (
        <div className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-surface p-3">
          <Field label="Prepiši iz fakture/predračuna (ID dokumenta)" className="grow"><input className={inputCls} value={fromDoc} onChange={(e) => setFromDoc(e.target.value)} placeholder="nije obavezno" /></Field>
          <Button variant="secondary" onClick={() => importDoc.mutate()} disabled={!fromDoc.trim() || importDoc.isPending}>Prepiši</Button>
        </div>
      )}

      <fieldset disabled={!editable} className="space-y-4">
        <Card title="Osnovno">
          <Field label="Broj otpremnice (prazno = automatski)"><input className={inputCls} value={number} onChange={(e) => setNumber(e.target.value)} placeholder="OT-2026-0001" /></Field>
          <Field label="Tip">
            <select className={inputCls} value={d.kind ?? "Ext"} onChange={(e) => set({ kind: e.target.value as "Ext" | "Int" })}>
              <option value="Ext">Eksterna (ka drugom licu)</option><option value="Int">Interna</option>
            </select>
          </Field>
          <Field label="Povezana narudžbenica / faktura"><input className={inputCls} value={d.orderRef ?? ""} onChange={(e) => set({ orderRef: e.target.value })} /></Field>
          <Field label="Napomena"><input className={inputCls} value={d.note ?? ""} onChange={(e) => set({ note: e.target.value })} /></Field>
        </Card>

        <Card title="Primalac">
          <Field label="Naziv"><input className={inputCls} value={d.recipient.name} onChange={(e) => setRecipient({ name: e.target.value })} /></Field>
          <Field label="PIB (9 cifara)"><input className={inputCls} value={d.recipient.pib ?? ""} onChange={(e) => setRecipient({ pib: e.target.value.replace(/\D/g, "").slice(0, 9) })} /></Field>
          <Field label="Matični broj"><input className={inputCls} value={d.recipient.mb ?? ""} onChange={(e) => setRecipient({ mb: e.target.value })} /></Field>
          <Field label="Ulica"><input className={inputCls} value={d.recipient.street ?? ""} onChange={(e) => setRecipient({ street: e.target.value })} /></Field>
          <Field label="Broj"><input className={inputCls} value={d.recipient.houseNumber ?? ""} onChange={(e) => setRecipient({ houseNumber: e.target.value })} /></Field>
          <Field label="Mesto"><input className={inputCls} value={d.recipient.city ?? ""} onChange={(e) => setRecipient({ city: e.target.value })} /></Field>
          <Field label="Poštanski broj"><input className={inputCls} value={d.recipient.postalCode ?? ""} onChange={(e) => setRecipient({ postalCode: e.target.value })} /></Field>
          <Field label="Kontakt osoba"><input className={inputCls} value={d.recipient.contactName ?? ""} onChange={(e) => setRecipient({ contactName: e.target.value })} /></Field>
          <Field label="Telefon"><input className={inputCls} value={d.recipient.phone ?? ""} onChange={(e) => setRecipient({ phone: e.target.value })} /></Field>
        </Card>

        <Card title="Prevoz i vreme">
          <Field label="Način otpreme">
            <select className={inputCls} value={d.method} onChange={(e) => set({ method: Number(e.target.value) as EotpDataDto["method"] })}>
              {Object.entries(METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="Otprema (datum i vreme)"><input type="datetime-local" className={inputCls} value={d.actualDespatchAt} onChange={(e) => set({ actualDespatchAt: e.target.value })} /></Field>
          <Field label="Planirani završetak isporuke"><input type="datetime-local" className={inputCls} value={d.plannedDeliveryEndAt} onChange={(e) => set({ plannedDeliveryEndAt: e.target.value })} /></Field>
          {[1, 2, 3].includes(d.method) && <>
            <Field label="Registarski broj vozila"><input className={inputCls} value={d.vehiclePlate ?? ""} onChange={(e) => set({ vehiclePlate: e.target.value })} /></Field>
            <Field label="Vozač: ime"><input className={inputCls} value={d.driver?.firstName ?? ""} onChange={(e) => set({ driver: { ...d.driver, firstName: e.target.value } })} /></Field>
            <Field label="Vozač: prezime"><input className={inputCls} value={d.driver?.lastName ?? ""} onChange={(e) => set({ driver: { ...d.driver, lastName: e.target.value } })} /></Field>
            <Field label="Vozač: e-pošta (za mobilnu aplikaciju)"><input className={inputCls} value={d.driver?.email ?? ""} onChange={(e) => set({ driver: { ...d.driver, email: e.target.value } })} /></Field>
          </>}
          {d.method === 2 && <>
            <Field label="Prevoznik: naziv"><input className={inputCls} value={d.carrier?.name ?? ""} onChange={(e) => set({ carrier: { ...d.carrier, name: e.target.value } })} /></Field>
            <Field label="Prevoznik: PIB"><input className={inputCls} value={d.carrier?.pib ?? ""} onChange={(e) => set({ carrier: { name: d.carrier?.name ?? "", ...d.carrier, pib: e.target.value.replace(/\D/g, "").slice(0, 9) } })} /></Field>
          </>}
          {[4, 5].includes(d.method) && <>
            <Field label="Kurir: ime"><input className={inputCls} value={d.courier?.firstName ?? ""} onChange={(e) => set({ courier: { ...d.courier, firstName: e.target.value } })} /></Field>
            <Field label="Kurir: prezime"><input className={inputCls} value={d.courier?.lastName ?? ""} onChange={(e) => set({ courier: { ...d.courier, lastName: e.target.value } })} /></Field>
            <Field label="Kurir: broj lične karte"><input className={inputCls} value={d.courier?.idNumber ?? ""} onChange={(e) => set({ courier: { ...d.courier, idNumber: e.target.value } })} /></Field>
          </>}
          <Field label="Odredište: ulica (ako nije adresa primaoca)"><input className={inputCls} value={d.deliveryAddress?.street ?? ""} onChange={(e) => set({ deliveryAddress: { ...d.deliveryAddress, street: e.target.value } })} /></Field>
          <Field label="Odredište: mesto"><input className={inputCls} value={d.deliveryAddress?.city ?? ""} onChange={(e) => set({ deliveryAddress: { ...d.deliveryAddress, city: e.target.value } })} /></Field>
          <Field label="Napomena za isporuku"><input className={inputCls} value={d.deliveryInstructions ?? ""} onChange={(e) => set({ deliveryInstructions: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!d.goodsReturn} onChange={(e) => set({ goodsReturn: e.target.checked })} /> Povraćaj robe</label>
        </Card>

        <div className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
          <h2 className="mb-2 text-sm font-semibold">Stavke</h2>
          <div className="space-y-2">
            {d.items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <input className={inputCls + " col-span-5"} placeholder="Naziv" value={it.name} onChange={(e) => set({ items: d.items.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                <input className={inputCls + " col-span-2"} placeholder="Šifra" value={it.code ?? ""} onChange={(e) => set({ items: d.items.map((x, j) => (j === i ? { ...x, code: e.target.value } : x)) })} />
                <input className={inputCls + " col-span-2"} type="number" step="any" min="0" value={it.quantity} onChange={(e) => set({ items: d.items.map((x, j) => (j === i ? { ...x, quantity: Number(e.target.value) } : x)) })} />
                <input className={inputCls + " col-span-2"} placeholder="Jed. mere" value={it.unit} onChange={(e) => set({ items: d.items.map((x, j) => (j === i ? { ...x, unit: e.target.value } : x)) })} />
                <button type="button" className="col-span-1 text-slate-400 hover:text-red-600" onClick={() => set({ items: d.items.filter((_, j) => j !== i) })} title="Ukloni">✕</button>
              </div>
            ))}
          </div>
          <Button className="mt-2" variant="secondary" onClick={() => set({ items: [...d.items, { name: "", quantity: 1, unit: "kom" }] })}>+ Stavka</Button>
        </div>
      </fieldset>

      <div className="flex flex-wrap items-center gap-2">
        {editable && <>
          <Button variant="secondary" onClick={() => save.mutate()} disabled={busy}>Sačuvaj nacrt</Button>
          <Button variant="secondary" onClick={() => doValidate.mutate()} disabled={busy}>Proveri u sistemu</Button>
          <Button onClick={() => doSend.mutate()} disabled={busy}>{doSend.isPending ? "Šaljem…" : "Pošalji otpremnicu"}</Button>
          {rec?.status === "DRAFT" && <Button variant="secondary" onClick={() => { if (confirm("Obrisati nacrt?")) del.mutate(); }}>Obriši nacrt</Button>}
        </>}
        {rec?.remote_id && <>
          <Button variant="secondary" onClick={() => open("pdf")}>PDF</Button>
          <Button variant="secondary" onClick={() => open("xml")}>XML</Button>
        </>}
      </div>
      {rec?.status === "REGISTERED" && (
        <div className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-surface p-3">
          <Field label="Razlog storna" className="grow"><input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
          <Button variant="secondary" onClick={() => { if (confirm("Stornirati otpremnicu?")) doCancel.mutate(); }} disabled={!reason.trim() || doCancel.isPending}>Storniraj</Button>
        </div>
      )}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
      <h2 className="mb-2 text-sm font-semibold">{title}</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </div>
  );
}

/** Uklanja prazne tekstove da server ne dobije "" umesto izostavljenog polja. */
function clean(d: EotpDataDto): EotpDataDto {
  const strip = <T,>(o: T): T => {
    if (Array.isArray(o)) return o.map(strip) as unknown as T;
    if (o && typeof o === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(o as Record<string, unknown>)) {
        if (v === "" || v === undefined || v === null) continue;
        out[k] = strip(v);
      }
      return out as T;
    }
    return o;
  };
  const c = strip(d);
  if (c.carrier && !c.carrier.name) delete c.carrier;
  return c;
}
